import { Button, buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  showMatchInboxSelection,
  toggleAllMatchesAction,
  updateUserMatchesAction
} from '../../../app/redux/actions/userMatchActions';
import { AppDispatch } from '../../../app/redux/configureStore';
import { BookmarkInfo } from '../../../app/redux/features/bookmark/bookmarkSlice';
import { getBookmarks } from '../../../app/redux/features/bookmark/getBookmarks';
import { useAppDispatch, useAppSelector } from '../../../app/redux/hooks';
import { UserMatchAction, UserMatchCollectStatus } from '../../../app/redux/reducers/userMatchesReducer';
import Follow from '../../bookmarks/Follow';
import Save from '../../bookmarks/Save';
import { MatchMessageNotification } from './MatchMessageNotification';
import { useUserMatches } from './hooks/UseUserMatches';

type MatchFooterProps = {
  toggleAllMatches: AppDispatch;
  transitionHandler: Function;
  currentItemIndex: number | undefined;
  transitionAllHandler: Function;
  updateMatches: AppDispatch;
  showInboxSelection: AppDispatch;
  nextItem: () => void;
  previousItem: () => void;
};

const displayPageNumber = (total: number, current: number | undefined) => {
  if (current === undefined) return;
  return `${current + 1} of ${total}`;
};

const MatchFooter = ({
  toggleAllMatches,
  transitionHandler,
  currentItemIndex,
  transitionAllHandler,
  nextItem,
  previousItem
}: MatchFooterProps) => {
  const bookmarkInfo = useAppSelector((state) => state.bookmarkInfo);
  const dispatch = useAppDispatch();
  React.useEffect(() => {
    if (bookmarkInfo.status == 'idle') {
      dispatch(getBookmarks());
    }
  }, []);

  const { showInbox, showMatchSelection, status } = useAppSelector((state) => state.matches);
  const { matches, selectedMatches, areAllMessagesAction } = useUserMatches();
  const [actionInProgress, setActionInProgress] = useState<UserMatchAction>();
  const isAllSelected = selectedMatches.length === matches.length;
  const contentType = matches[currentItemIndex!]?.contentType;
  const showMarkUnread = areAllMessagesAction(selectedMatches, 'read');
  const showMarkRead = areAllMessagesAction(selectedMatches, 'unread');
  const dismissedLoading = status === UserMatchCollectStatus.Pending && actionInProgress === UserMatchAction.Dismissed;
  const userMatchId = matches[currentItemIndex!]?.contentId ?? 0;

  return (
    ((showInbox && showMatchSelection) || (!showInbox && matches.length > 0)) && (
      <div
        id="match-footer"
        className={`absolute bottom-0 w-full flex flex-col justify-center bg-white ${showInbox ? 'h-[60px]' : ''}`}
      >
        {showInbox ? (
          <>
            {showMatchSelection && matches.length > 0 && (
              <div className="flex px-5 justify-between">
                <Button
                  size="sm"
                  appearance="primary"
                  variant="bare"
                  data-cy={`button:select all`}
                  onClick={() => {
                    toggleAllMatches(isAllSelected ? false : true);
                  }}
                >
                  {isAllSelected ? 'Unselect all' : 'Select all'}
                </Button>
                <div>
                  {selectedMatches.length > 0 && (
                    <div className="flex justify-between gap-3">
                      {showMarkUnread || showMarkRead ? (
                        <Button
                          size="sm"
                          variant="bare"
                          data-cy={showMarkUnread ? `button:Mark as unread` : `button:Mark as read`}
                          onClick={() =>
                            showMarkUnread
                              ? transitionAllHandler(UserMatchAction.Seen, false)
                              : transitionAllHandler(UserMatchAction.Seen, true)
                          }
                        >
                          {showMarkUnread ? <>Unread</> : <>Read</>}
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        appearance="danger"
                        variant="bare"
                        data-cy={`button:delete message`}
                        onClick={() => transitionAllHandler(UserMatchAction.Dismissed, true)}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          matches.length > 0 && (
            <>
              <div className="w-full flex justify-center ">
                {userMatchId in bookmarkInfo.bookmarks && <MatchMessageNotification contentType={contentType} />}
              </div>
              <div className="flex items-center p-4 md:px-4 space-x-4 pt-5 border-t-[1px] border-gray-200">
                <Button
                  appearance="gray"
                  variant="outline"
                  className="w-full"
                  loading={dismissedLoading}
                  onClick={() => {
                    setActionInProgress(UserMatchAction.Dismissed);
                    transitionHandler(UserMatchAction.Dismissed);
                  }}
                  data-cy={`button:dismiss-match`}
                >
                  <FontAwesomeIcon icon={['far', 'trash']} className={`w-4`} />
                  <span>No thanks</span>
                </Button>
                {contentType === 'organisation' || contentType === 'education_provider' ? (
                  <div className="w-full justify-center">
                    <Follow
                      id={`${matches[currentItemIndex!]?.contentId}`}
                      showToast={false}
                      bookmarkBtnId={`match_${matches[currentItemIndex!]?.contentId}`}
                      callback={(bookmarkData: BookmarkInfo) => {
                        if (matches[currentItemIndex!]?.contentId in bookmarkData.bookmarks) {
                          transitionHandler(UserMatchAction.Saved);
                        }
                      }}
                      className={cn(
                        buttonVariants({
                          appearance: 'gray',
                          variant: 'outline',
                          className: 'w-full capitalize tracking-normal'
                        })
                      )}
                    />
                  </div>
                ) : (
                  <div className="w-full justify-center">
                    <Save
                      id={`${matches[currentItemIndex!]?.contentId}`}
                      showToast={false}
                      bookmarkBtnId={`match_${matches[currentItemIndex!]?.contentId}`}
                      contentType={matches[currentItemIndex]?.contentType}
                      callback={(bookmarkData: BookmarkInfo) => {
                        if (matches[currentItemIndex!]?.contentId in bookmarkData.bookmarks) {
                          transitionHandler(UserMatchAction.Saved);
                        }
                      }}
                      className={cn(
                        buttonVariants({
                          appearance: 'gray',
                          variant: 'outline',
                          className: 'w-full capitalize tracking-normal'
                        })
                      )}
                    />
                  </div>
                )}
              </div>
              {matches.length > 1 && (
                <div className="w-full flex justify-between items-center text-shock px-5 pb-5">
                  <FontAwesomeIcon
                    icon={['far', 'chevron-left']}
                    className={`w-5 h-5 ${currentItemIndex === 0 && 'invisible'} cursor-pointer`}
                    onClick={previousItem}
                  />
                  <div className="inline-block">
                    <FontAwesomeIcon icon={['fas', 'message-lines']} className="text-shock w-5 h-5 relative top-0.5" />
                    <span className="font-semibold ml-4 text-sm">
                      {displayPageNumber(matches.length, currentItemIndex)}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    icon={['far', 'chevron-right']}
                    className={`w-5 h-5 ${currentItemIndex === matches.length - 1 && 'invisible'} cursor-pointer`}
                    onClick={nextItem}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>
    )
  );
};

const mapStateToProps = ({ auth }: any) => {
  return { matches: auth.matches };
};

const mapActionsToProps = {
  updateMatches: updateUserMatchesAction,
  toggleAllMatches: toggleAllMatchesAction,
  showInboxSelection: showMatchInboxSelection
};

export default connect(mapStateToProps, mapActionsToProps)(MatchFooter);
